import { useState, useEffect, useRef } from "react";
import { moveInArray, haStella } from "./comuni";

/**
 * Lista di tratte riordinabile con stelle di priorità. È la colonna "Selezionati" del
 * modale "Genera percorso operativo" (TabAnagraficaPercorsi) estratta in un componente.
 *
 * Props:
 *  - tratti       [{ id, codice, strada, tratto, passAnno, prioritaria }]
 *  - onChange     setter di useState del padre: viene chiamato con una FUNZIONE (arr => next),
 *                 così due eventi ravvicinati (drag) lavorano sempre sull'array più recente.
 *  - onRemove     (id) => void; se passato compare il bottone "Rimuovi" su ogni riga
 *  - recuperoMap  { codice: { livello: "gialla"|"rossa", consecutive } } stelle automatiche
 *  - disabled     blocca le interazioni (mentre si salva)
 *
 * Tre modi di riordinare, come nel modale Genera: maniglia drag-and-drop (desktop),
 * frecce su/giù (mobile, dove il drag col dito dava problemi), numerino editabile.
 */
export default function ListaTrattiOrdinabile({ tratti, onChange, onRemove = null, recuperoMap = {}, disabled = false }) {
    const [dragIndex, setDragIndex] = useState(null);
    const [movedId, setMovedId] = useState(null);
    const movedTimerRef = useRef(null);
    const [idxEditId, setIdxEditId] = useState(null);
    const [idxEditVal, setIdxEditVal] = useState("");

    const move = (from, to) => onChange((arr) => moveInArray(arr, from, to));

    // Evidenzia per ~1s la riga appena spostata, così non la si perde di vista.
    const flashMoved = (id) => {
        setMovedId(id);
        if (movedTimerRef.current) clearTimeout(movedTimerRef.current);
        movedTimerRef.current = setTimeout(() => setMovedId(null), 1000);
    };
    useEffect(() => () => { if (movedTimerRef.current) clearTimeout(movedTimerRef.current); }, []);

    // Porta in vista la riga spostata (il box ha scroll interno). requestAnimationFrame:
    // aspetto che React abbia ridisegnato la lista nel nuovo ordine.
    const scrollToTratto = (id) => {
        requestAnimationFrame(() => {
            document.querySelector(`[data-tratto-id="${id}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        });
    };

    // Stella manuale: accenderla porta la tratta in testa, in coda alle stellate già lì;
    // spegnerla cambia solo il colore (da lì la si sposta a mano). Se ha già la stella
    // automatica è già nel blocco in testa e non si muove.
    const toggleStella = (id) => {
        onChange((arr) => {
            const from = arr.findIndex((t) => t.id === id);
            if (from < 0) return arr;
            const accesa = !arr[from].prioritaria;
            const next = arr.map((t) => (t.id === id ? { ...t, prioritaria: accesa } : t));
            if (!accesa || haStella(arr[from], recuperoMap)) return next;
            let to = 0;
            while (to < arr.length && haStella(arr[to], recuperoMap)) to++;
            return moveInArray(next, from, to);
        });
        flashMoved(id);
        scrollToTratto(id);
    };

    // Numerino: 1 = prima posizione; fuori range viene "pinzato"; vuoto/Esc = nessuno spostamento.
    const applyIdxEdit = (from) => {
        const raw = idxEditVal;
        setIdxEditId(null);
        setIdxEditVal("");
        const n = parseInt(raw, 10);
        if (!Number.isFinite(n)) return;
        const to = Math.min(Math.max(n, 1), tratti.length) - 1;
        if (to === from) return;
        const id = tratti[from]?.id;
        move(from, to);
        if (id != null) { flashMoved(id); scrollToTratto(id); }
    };

    if (!tratti.length) return <div className="gp-box-empty">Nessuna tratta selezionata. Aggiungila dalla colonna a sinistra.</div>;

    return tratti.map((t, i) => {
        const auto = recuperoMap[t.codice];
        const accesa = !!auto || !!t.prioritaria;
        const rossa = auto?.livello === "rossa";
        const volte = auto?.consecutive || 0;
        const titoloAuto = `Non effettuata ${volte} volt${volte === 1 ? "a" : "e"} di fila — da recuperare`;
        return (
            <div
                className={`tratto-row ${rossa ? "is-recupero-rossa" : accesa ? "is-prioritaria" : ""} ${dragIndex === i ? "gp-dragging" : ""} ${movedId === t.id ? "gp-just-moved" : ""}`}
                key={t.id}
                data-row
                data-tratto-id={t.id}
                onDragOver={(e) => { if (dragIndex === null || disabled) return; e.preventDefault(); if (dragIndex !== i) { move(dragIndex, i); setDragIndex(i); } }}
                onDrop={(e) => e.preventDefault()}
            >
                <span
                    className="gp-drag-handle"
                    draggable={!disabled}
                    onDragStart={(e) => {
                        const row = e.currentTarget.closest("[data-row]");
                        const rect = row.getBoundingClientRect();
                        e.dataTransfer.effectAllowed = "move";
                        try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) { /* Safari */ }
                        e.dataTransfer.setDragImage(row, e.clientX - rect.left, e.clientY - rect.top);
                        setDragIndex(i);
                    }}
                    onDragEnd={() => setDragIndex(null)}
                    title="Trascina per riordinare"
                    aria-label="Trascina per riordinare questa tratta"
                    role="button"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
                </span>
                <div className="gp-move-col">
                    <button className="gp-move-btn" title="Sposta su" disabled={disabled || i === 0} onClick={() => { move(i, i - 1); flashMoved(t.id); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 15l-6-6-6 6" /></svg>
                    </button>
                    <button className="gp-move-btn" title="Sposta giù" disabled={disabled || i === tratti.length - 1} onClick={() => { move(i, i + 1); flashMoved(t.id); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6" /></svg>
                    </button>
                </div>
                <input
                    type="text"
                    inputMode="numeric"
                    className="idx idx-input"
                    value={idxEditId === t.id ? idxEditVal : i + 1}
                    title="Scrivi la posizione e premi Invio"
                    aria-label={`Posizione di ${t.strada || t.codice}: ${i + 1}`}
                    disabled={disabled}
                    onFocus={(e) => { setIdxEditId(t.id); setIdxEditVal(String(i + 1)); e.target.select(); }}
                    onChange={(e) => setIdxEditVal(e.target.value.replace(/\D/g, "").slice(0, 3))}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                        if (e.key === "Escape") { setIdxEditVal(String(i + 1)); e.currentTarget.blur(); }
                    }}
                    onBlur={() => applyIdxEdit(i)}
                />
                <div className="t-name"><b>{t.strada || "(senza strada)"}</b><span>{t.tratto} · {t.codice}{t.passAnno != null && <span className="gp-pass-badge">{t.passAnno} pass/anno</span>}</span></div>
                <button
                    className={`icon-btn gp-star-btn ${accesa ? "on" : ""} ${rossa ? "gp-star-rossa" : ""}`}
                    disabled={disabled}
                    title={[auto ? titoloAuto : null, t.prioritaria ? "Prioritaria per questo percorso — clic per togliere" : "Segna prioritaria per questo percorso"].filter(Boolean).join(" · ")}
                    onClick={() => toggleStella(t.id)}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={accesa ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                </button>
                {/* Rimozione DIRETTA (no conferma), come nel Genera: tocca solo questo giro. */}
                {onRemove && (
                    <button className="icon-btn danger" title="Rimuovi" disabled={disabled} onClick={() => onRemove(t.id)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                    </button>
                )}
            </div>
        );
    });
}
